import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddDealDrawer from "./AddDealDrawer";
import { getDealsList } from "../api/auth";


import {
  Mail,
  Pencil,
  MessageCircle,
  Download,
  Trash2,
  Upload,
  RefreshCw,
  Loader2,
  MoreHorizontal,
  Briefcase,
  Phone,
  ShoppingBag,
  Wallet,
  CreditCard,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Users,
  X,
} from "lucide-react";

// Converts an array of row objects into a downloadable CSV file using the
// given column definitions, then triggers the browser's save dialog.
const exportRowsToCSV = (rows, columns, filename) => {
  const escapeCsvValue = (value) => {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const headerLine = columns.map((col) => escapeCsvValue(col.label)).join(",");
  const bodyLines = rows.map((row) =>
    columns.map((col) => escapeCsvValue(col.value(row))).join(",")
  );
  const csvContent = [headerLine, ...bodyLines].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Small searchable multi-select used by the filter panel (type to search,
// pick from the dropdown, remove via the chip's x). Mirrors the same
// component used on the Opportunities page so filtering feels consistent
// across list pages.
function MultiSelectFilterField({ label, placeholder, options, selectedValues, onChange, onClear }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(
    (opt) => !selectedValues.includes(opt) && opt.toLowerCase().includes(search.toLowerCase())
  );

  const addValue = (value) => {
    onChange([...selectedValues, value]);
    setSearch("");
  };

  const removeValue = (value) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-xs font-bold text-slate-600">{label}</p>
        {onClear && selectedValues.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClear}
            className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 pl-2 pr-1 py-0.5 text-[11px] font-medium max-w-full"
            >
              <span className="truncate">{value}</span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => removeValue(value)}
                className="shrink-0 text-blue-400 hover:text-blue-700 cursor-pointer"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1">
            {filteredOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addValue(opt)}
                className="px-2.5 py-1.5 text-sm text-slate-700 hover:bg-blue-50 cursor-pointer truncate"
              >
                {opt}
              </div>
            ))}
          </div>
        )}

        {isOpen && search && filteredOptions.length === 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 rounded-lg border border-slate-200 bg-white shadow-lg py-2 px-2.5 text-xs text-slate-400">
            No matches
          </div>
        )}
      </div>
    </div>
  );
}

export default function Deals() {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const amMenuRef = useRef(null);

  const [dealsData, setDealsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState("");
  const [openAddDeal, setOpenAddDeal] = useState(false);

  const [activeFilter, setActiveFilter] = useState("All");
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [amMenuOpen, setAmMenuOpen] = useState(false);

  // Account Manager scoping (admin only — see below)
  const [accountManagerFilter, setAccountManagerFilter] = useState([]);
  const [viewAll, setViewAll] = useState(false);

  // Extra filter-panel fields (beyond Account Manager)
  const [companyFilter, setCompanyFilter] = useState([]);
  const [poNumberFilter, setPoNumberFilter] = useState("");
  const [poValueMin, setPoValueMin] = useState("");
  const [poValueMax, setPoValueMax] = useState("");
  const [lastUpdatedFrom, setLastUpdatedFrom] = useState("");
  const [lastUpdatedTo, setLastUpdatedTo] = useState("");

  // Sorting & Pagination
  const [sortField, setSortField] = useState("lead_id");
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" | "desc"
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
    setSelected([]);
  };

  const NUMERIC_SORT_FIELDS = ["lead_id", "opf_cust_po_value", "pcount", "opf_last_updated"];

  const getSortValue = (deal, field) => {
    switch (field) {
      case "lead_id":
        return Number(deal.lead_id ?? deal.opf_id) || 0;
      case "company_name":
        return deal.company_name || "";
      case "account_manager":
        return deal.account_manager || "";
      case "po_no":
        return deal.po_no || "";
      case "opf_cust_po_value":
        return Number(deal.opf_cust_po_value) || 0;
      case "pcount":
        return Number(deal.pcount) || 0;
      case "opf_last_updated": {
        const parsed = new Date(deal.opf_last_updated || 0);
        return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
      }
      default:
        return "";
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
const department = (user.department || "").toLowerCase();
const isAdmin = department === "admin";

  // Arriving from a specific user's dashboard (Users list -> that user's
  // dashboard -> a stat card) pre-selects that person as the Account
  // Manager instead of showing a blank page.
  useEffect(() => {
    if (location.state?.accountManager) {
      setAccountManagerFilter([location.state.accountManager]);
      setViewAll(false);
    } else if (location.state?.viewAll) {
      setAccountManagerFilter([]);
      setViewAll(true);
    }
  }, [location.state]);

  // Admin-only: bypass the "pick an Account Manager first" restriction and
  // show every deal at once. Acts as a toggle — clicking again while active
  // ("Hide All") turns it back off and returns to the blank state.
  const handleViewAll = () => {
    setAccountManagerFilter([]);
    setViewAll((prev) => !prev);
  };

  /* ==========================================================
      FETCH DEALS
  ========================================================== */

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);

      const response = await getDealsList();

      if (response.data && response.data.success) {
        setDealsData(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching deals:", err);
      setToast("Unable to load deals. Please try again.");
      setTimeout(() => setToast(""), 2000);
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
      FILTERS
  ========================================================== */

  const accountManagerOptions = Array.from(
    new Set(dealsData.map((d) => d.account_manager).filter(Boolean))
  ).sort();
  const companyOptions = Array.from(
    new Set(dealsData.map((d) => d.company_name).filter(Boolean))
  ).sort();

  const isAdminBlankState = isAdmin && !viewAll && accountManagerFilter.length === 0;

  const filtered = (isAdminBlankState ? [] : dealsData).filter((deal) => {
    const searchTerm = search.toLowerCase();
    const matchesSearch =
      // The table's "Opportunity Id" column shows deal.lead_id — that's what
      // this search box's own placeholder promises to match against.
      String(deal.lead_id ?? "")
        .toLowerCase()
        .includes(searchTerm) ||
      String(deal.opf_id ?? "")
        .toLowerCase()
        .includes(searchTerm) ||
      (deal.company_name || "")
        .toLowerCase()
        .includes(searchTerm) ||
      (deal.account_manager || "")
        .toLowerCase()
        .includes(searchTerm) ||
      (deal.account_manager_email || "")
        .toLowerCase()
        .includes(searchTerm);

    const matchesFilter =
      activeFilter === "All"
        ? true
        : activeFilter === "Started"
        ? deal.pcount !== 0
        : activeFilter === "Not Started"
        ? deal.pcount === 0
        : deal.status === activeFilter;

    const matchesAccountManager =
      accountManagerFilter.length === 0 || accountManagerFilter.includes(deal.account_manager || "");

    const matchesCompany =
      companyFilter.length === 0 || companyFilter.includes(deal.company_name || "");

    const matchesPoNumber =
      !poNumberFilter || (deal.po_no || "").toLowerCase().includes(poNumberFilter.toLowerCase());

    const dealPoValue = Number(deal.opf_cust_po_value) || 0;
    const matchesPoValueMin = poValueMin === "" || dealPoValue >= Number(poValueMin);
    const matchesPoValueMax = poValueMax === "" || dealPoValue <= Number(poValueMax);

    const dealLastUpdated = new Date(deal.opf_last_updated || 0);
    const matchesLastUpdatedFrom =
      !lastUpdatedFrom ||
      (!isNaN(dealLastUpdated.getTime()) && dealLastUpdated >= new Date(lastUpdatedFrom));
    const matchesLastUpdatedTo =
      !lastUpdatedTo ||
      (!isNaN(dealLastUpdated.getTime()) && dealLastUpdated <= new Date(`${lastUpdatedTo}T23:59:59`));

    return (
      matchesSearch &&
      matchesFilter &&
      matchesAccountManager &&
      matchesCompany &&
      matchesPoNumber &&
      matchesPoValueMin &&
      matchesPoValueMax &&
      matchesLastUpdatedFrom &&
      matchesLastUpdatedTo
    );
  }).sort((a, b) => {
    const valA = getSortValue(a, sortField);
    const valB = getSortValue(b, sortField);

    if (NUMERIC_SORT_FIELDS.includes(sortField)) {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }

    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    if (strA < strB) return sortOrder === "asc" ? -1 : 1;
    if (strA > strB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const selectedDeals = dealsData.filter((deal) =>
    selected.includes(deal.opf_id)
  );

  const activeFilterCount =
    companyFilter.length +
    (poNumberFilter ? 1 : 0) +
    (poValueMin !== "" ? 1 : 0) +
    (poValueMax !== "" ? 1 : 0) +
    (lastUpdatedFrom ? 1 : 0) +
    (lastUpdatedTo ? 1 : 0);

  /* PAGINATION (based on sorted + filtered results) */
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedDeals = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    activeFilter,
    accountManagerFilter,
    companyFilter,
    poNumberFilter,
    poValueMin,
    poValueMax,
    lastUpdatedFrom,
    lastUpdatedTo,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  /* ==========================================================
      OUTSIDE CLICK
  ========================================================== */

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setActiveDropdownId(null);
      }
      if (
        filterMenuOpen &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
      if (
        amMenuOpen &&
        amMenuRef.current &&
        !amMenuRef.current.contains(event.target)
      ) {
        setAmMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
  }, [filterMenuOpen, amMenuOpen]);

  /* ==========================================================
      SELECT
  ========================================================== */

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(filtered.map((deal) => deal.opf_id));
    } else {
      setSelected([]);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  /* ==========================================================
      ACTIONS
  ========================================================== */

  const handleExport = () => {
    const columns = [
      { label: "Opportunity ID", value: (d) => d.lead_id ?? d.opf_id ?? "" },
      { label: "Company", value: (d) => d.company_name || "" },
      { label: "Account Manager", value: (d) => d.account_manager || "" },
      { label: "Account Manager Email", value: (d) => d.account_manager_email || "" },
      { label: "Customer PO Number", value: (d) => d.po_no || "" },
      { label: "Customer PO Value", value: (d) => d.opf_cust_po_value ?? "" },
      { label: "F1 PO Raised", value: (d) => (d.pcount === 0 ? "Not Started" : "Started") },
      {
        label: "Last Updated",
        value: (d) =>
          d.opf_last_updated ? new Date(d.opf_last_updated).toLocaleDateString("en-IN") : "",
      },
    ];

    if (filtered.length === 0) {
      setToast("No orders to export");
      setTimeout(() => setToast(""), 2000);
      return;
    }

    exportRowsToCSV(filtered, columns, `Orders_export_${new Date().toISOString().slice(0, 10)}.csv`);

    setToast("Orders exported to CSV");
    setTimeout(() => setToast(""), 2000);
  };

  const handleImport = () => {
    setToast("File uploaded successfully ✅");

    setTimeout(() => setToast(""), 2000);
  };

  const handleSync = async () => {
    setSyncing(true);

    await fetchDeals();

    setSyncing(false);

    setToast("Sync completed ✅");

    setTimeout(() => setToast(""), 2000);
  };

 const openDeal = (opfId) => {
  // Default view matches the user's department
  localStorage.setItem("dealViewMode", department);

  navigate(`/deals/${opfId}`);
};

  return (
    <div className="ml-[60px]  p-3 sm:p-6 bg-[#F1F5F9] min-h-screen pt-16  mt-12 md:h-[calc(100vh-3rem)] md:min-h-0 md:flex md:flex-col md:overflow-hidden">
      {/* TOAST */}
      {toast && (
        <div className="fixed top-20 right-6 bg-black text-white px-4 py-2 rounded shadow z-50 text-sm">
          {toast}
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800">
            Orders
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Orders &gt; List | Total Orders: {filtered.length}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <div className="relative" ref={amMenuRef}>
                <button
                  onClick={() => setAmMenuOpen((prev) => !prev)}
                  className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    amMenuOpen || accountManagerFilter.length > 0
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <Users size={14} />
                  Account Manager
                  {accountManagerFilter.length > 0 && (
                    <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                      {accountManagerFilter.length}
                    </span>
                  )}
                </button>

                {amMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-64 max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                    <MultiSelectFilterField
                      label="Account Manager"
                      placeholder="Type to search..."
                      options={accountManagerOptions}
                      selectedValues={accountManagerFilter}
                      onChange={(value) => {
                        setViewAll(false);
                        setAccountManagerFilter(value);
                      }}
                      onClear={() => setAccountManagerFilter([])}
                    />
                  </div>
                )}
              </div>

              <button
                onClick={handleViewAll}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  viewAll
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {viewAll ? "Hide All" : "View All"}
              </button>

              <div className="relative" ref={filterMenuRef}>
                <button
                  onClick={() => setFilterMenuOpen((prev) => !prev)}
                  className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    filterMenuOpen || activeFilterCount > 0
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <Filter size={14} />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {filterMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-72 max-w-[90vw] max-h-[75vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                    <div className="flex items-center justify-between mb-2 sticky -top-3 -mt-3 -mx-3 px-3 pt-3 pb-2 bg-white border-b border-slate-100 z-10">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Filters
                      </p>
                      {activeFilterCount > 0 && (
                        <button
                          onClick={() => {
                            setCompanyFilter([]);
                            setPoNumberFilter("");
                            setPoValueMin("");
                            setPoValueMax("");
                            setLastUpdatedFrom("");
                            setLastUpdatedTo("");
                          }}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                        >
                          Clear all
                        </button>
                      )}
                    </div>

                    <MultiSelectFilterField
                      label="Company"
                      placeholder="Type to search..."
                      options={companyOptions}
                      selectedValues={companyFilter}
                      onChange={setCompanyFilter}
                    />

                    <div className="mb-3">
                      <p className="mb-1.5 text-xs font-bold text-slate-600">Customer PO Number</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={poNumberFilter}
                        onChange={(event) => setPoNumberFilter(event.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="mb-3">
                      <p className="mb-1.5 text-xs font-bold text-slate-600">Customer PO Value</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="Min"
                          value={poValueMin}
                          onChange={(event) => setPoValueMin(event.target.value)}
                          className="w-1/2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Max"
                          value={poValueMax}
                          onChange={(event) => setPoValueMax(event.target.value)}
                          className="w-1/2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">Last Updated</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={lastUpdatedFrom}
                          onChange={(event) => setLastUpdatedFrom(event.target.value)}
                          className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <input
                          type="date"
                          value={lastUpdatedTo}
                          onChange={(event) => setLastUpdatedTo(event.target.value)}
                          className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <button
            onClick={handleExport}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer transition"
          >
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* FILTER + SEARCH */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            placeholder="Search by Opportunity ID, Company or Account Manager..."
            className="w-full sm:flex-1 sm:min-w-[250px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button
            onClick={() => {
              setActiveFilter("All");
              setSelected([]);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              activeFilter === "All"
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            All Deals
          </button>

          <button
            onClick={() => {
              setActiveFilter("Started");
              setSelected([]);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              activeFilter === "Started"
                ? "bg-green-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Started
          </button>

          <button
            onClick={() => {
              setActiveFilter("Not Started");
              setSelected([]);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              activeFilter === "Not Started"
                ? "bg-slate-700 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Not Started
          </button>
        </div>
      </div>

      {/* TABLE STARTS HERE */}
            {/* TABLE */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto max-h-[70vh] md:max-h-none md:flex-1 md:min-h-0 flex flex-col">
        {loading ? (
          <div className="flex-1 py-20 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <table className="w-full min-w-[1000px] text-sm shrink-0 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_thead_th]:shadow-[inset_0_-1px_0_#e2e8f0]">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 w-12 text-center">
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={
                        filtered.length > 0 &&
                        selected.length === filtered.length
                      }
                      className="cursor-pointer"
                    />
                  </th>

                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("lead_id")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Opportunity Id
                      {sortField === "lead_id" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("company_name")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Company
                      {sortField === "company_name" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("account_manager")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Account Manager
                      {sortField === "account_manager" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("po_no")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Customer PO Number
                      {sortField === "po_no" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("opf_cust_po_value")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Customer PO Value
                      {sortField === "opf_cust_po_value" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("pcount")}
                  >
                    <span className="inline-flex items-center gap-1">
                      F1 PO Raised
                      {sortField === "pcount" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort("opf_last_updated")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Last Updated
                      {sortField === "opf_last_updated" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                    </span>
                  </th>
                  <th className="p-3 w-16 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedDeals.map((deal) => (
                  <tr
                    key={deal.opf_id}
                    onClick={() => openDeal(deal.opf_id)}
                    className="border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer"
                  >
                    <td
                      className="p-3 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(deal.opf_id)}
                        onChange={() => toggleRow(deal.opf_id)}
                        className="cursor-pointer"
                      />
                    </td>

                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        

                        <div>

                          <div className="text-l  text-blue-700">
                            {deal.lead_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-slate-700">
                      {deal.company_name || "-"}
                    </td>

                    <td className="p-3 text-slate-700">
                      <div>{deal.account_manager || "-"}</div>
                      {deal.account_manager_email && (
                        <div className="text-xs text-slate-400">{deal.account_manager_email}</div>
                      )}
                    </td>

                    <td className="p-3 text-slate-700">
  {deal.po_no || "-"}
</td>

                    

                    <td className="p-3 font-semibold text-slate-800">
                      ₹
                      {Number(
                        deal.opf_cust_po_value || 0
                      ).toLocaleString("en-IN")}
                    </td>

                    <td className="p-3">
  <span
    className={`rounded-full px-2.5 py-1 text-xs font-bold ${
      deal.pcount === 0
        ? "bg-slate-100 text-slate-700"
        : "bg-green-100 text-green-500"
    }`}
  >
    {deal.pcount === 0 ? "Not Started" : "Started"}
  </span>
</td>

                    <td className="p-3 text-xs text-slate-600">
                      {deal.opf_last_updated
                        ? new Date(
                            deal.opf_last_updated
                          ).toLocaleDateString("en-IN")
                        : "-"}
                    </td>

                    <td
                      className="p-3 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-block text-left"
                        ref={
                          activeDropdownId === deal.opf_id
                            ? dropdownRef
                            : null
                        }
                      >
                        <button
                          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800"
                          onClick={() =>
                            setActiveDropdownId(
                              activeDropdownId === deal.opf_id
                                ? null
                                : deal.opf_id
                            )
                          }
                        >
                          <MoreHorizontal size={16} />
                        </button>

                        {activeDropdownId === deal.opf_id && (
  <div className="absolute right-0 mt-1.5 w-40 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1.5">

    {department === "purchase" ? (
      <>
        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            localStorage.setItem("dealViewMode", "sales");

            navigate(`/deals/${deal.opf_id}`);
          }}
        >
          <Briefcase size={13} className="text-blue-600" />
          View Sales Details
        </button>

        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            localStorage.setItem("dealViewMode", "purchase");

            navigate(`/deals/${deal.opf_id}`);
          }}
        >
          <ShoppingBag size={13} className="text-amber-600" />
          View Purchase Details
        </button>
      </>
    ) : department === "finance" ? (
      <>
        

        <div className="h-px bg-slate-100 my-1"></div>

        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            localStorage.setItem("dealViewMode", "sales");

            navigate(`/deals/${deal.opf_id}`);
          }}
        >
          <Briefcase size={13} className="text-blue-600" />
          View Sales Details
        </button>

        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            localStorage.setItem("dealViewMode", "purchase");

            navigate(`/deals/${deal.opf_id}`);
          }}
        >
          <ShoppingBag size={13} className="text-amber-600" />
          View Purchase Details
        </button>
      </>
    ) : (
      <>
        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            localStorage.setItem("dealViewMode", "sales");

            navigate(`/deals/${deal.opf_id}`);
          }}
        >
          <Pencil size={13} className="text-blue-600" />
          View Details
        </button>

        {/* <div className="h-px bg-slate-100 my-1"></div> */}

        {/* <button
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 text-left"
          onClick={() => {
            setActiveDropdownId(null);

            if (window.confirm(`Delete Deal #${deal.opf_id}?`)) {
              console.log("Delete:", deal.opf_id);
            }
          }}
        >
          <Trash2 size={13} />
          Delete Deal
        </button> */}
      </>
    )}
  </div>
)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filtered.length === 0 && (
              <div className="flex-1 flex items-center justify-center py-16 text-center text-slate-500 text-sm font-medium px-4">
                {isAdminBlankState
                  ? "Select an Account Manager above (or click View All) to see orders."
                  : "No Orders Found"}
              </div>
            )}
          </>
        )}
      </div>

      {/* PAGINATION */}
      {!loading && filtered.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-2 shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              aria-label="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600 shadow-sm">
            <span className="text-slate-400">
              {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
            </span>
            <div className="h-4 w-px bg-slate-200" />
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-medium text-slate-700">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
            {/* ADD DEAL DRAWER */}
      <AddDealDrawer
        isOpen={openAddDeal}
        onClose={() => {
          setOpenAddDeal(false);
          fetchDeals(); // Refresh list after adding a deal
        }}
      />

      {/* OVERLAY */}
      {openDrawer && (
        <div
          onClick={() => setOpenDrawer(false)}
          className="fixed inset-0 bg-black/30 z-40"
        />
      )}

      {/* DETAILS DRAWER */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[320px] bg-white shadow-xl z-50 transform transition-transform duration-300 ${
          openDrawer ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-4 border-b font-semibold text-slate-800">
          {selected.length > 1 ? "Bulk Summary" : "Deal Details"}
        </div>

        <div className="p-4 space-y-4">
          {/* BULK SUMMARY */}
          {selected.length > 1 && (
            <>
              {selectedDeals.map((deal) => (
                <div
                  key={deal.opf_id}
                  className="text-xs text-slate-700"
                >
                  • Deal #{deal.opf_id}
                </div>
              ))}

              <button className="w-full bg-blue-600 text-white text-xs font-semibold py-2 rounded-lg mt-4 shadow-sm hover:bg-blue-700 transition">
                Apply Bulk Update
              </button>
            </>
          )}

          {/* SINGLE DEAL */}
          {selected.length === 1 && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center">
                  <Briefcase size={20} />
                </div>

                <div>
                  <div className="font-semibold text-slate-800">
                    Deal #{selectedDeals[0].opf_id}
                  </div>

                  <div className="text-xs text-slate-500">
                    OPF-{selectedDeals[0].opf_id}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 text-xs">
                <p>
                  <span className="text-slate-400 font-medium">
                    Company:
                  </span>{" "}
                  <span>
                    {selectedDeals[0].company_name || "-"}
                  </span>
                </p>

                <p>
                  <span className="text-slate-400 font-medium">
                    Value:
                  </span>{" "}
                  <span className="font-semibold text-slate-800">
                    ₹
                    {Number(
                      selectedDeals[0].opf_cust_po_value || 0
                    ).toLocaleString("en-IN")}
                  </span>
                </p>

                <p>
  <span className="text-slate-400 font-medium">
    PO Number:
  </span>{" "}
  <span className="font-semibold">
    {selectedDeals[0].po_no || "-"}
  </span>
</p>

                <p>
                  <span className="text-slate-400 font-medium">
                    Last Updated:
                  </span>{" "}
                  <span>
                    {selectedDeals[0].opf_last_updated
                      ? new Date(
                          selectedDeals[0].opf_last_updated
                        ).toLocaleDateString("en-IN")
                      : "-"}
                  </span>
                </p>
              </div>

              <div className="flex gap-4 mt-4 text-slate-500">
                <Phone
                  size={18}
                  className="cursor-pointer hover:text-slate-800 transition"
                />

                <MessageCircle
                  size={18}
                  className="cursor-pointer hover:text-slate-800 transition"
                />
              </div>
            </>
          )}

          {/* NOTHING SELECTED */}
          {selected.length === 0 && (
            <p className="text-slate-400 text-xs font-medium">
              No deal selected
            </p>
          )}
        </div>
      </div>

      {/* DRAWER TOGGLE BUTTON */}
      {/*
      <div
        onClick={() => setOpenDrawer(!openDrawer)}
        className="fixed right-0 top-1/2 h-[120px] w-[36px] bg-blue-600 text-white text-xs font-semibold flex items-center justify-center rotate-180 cursor-pointer z-50 shadow-md rounded-t-lg hover:bg-blue-700 transition"
        style={{ writingMode: "vertical-rl" }}
      >
        Details Panel
      </div>
      */}
    </div>
  );
}